import API from 'helpers/api';
import { TimeFilterProps } from 'hooks/useDateTimeFilter';
import { Pagination } from 'models/Pagination';
import { Manifest } from '../models/manifest';
import { IntegrationConfig } from 'models/integrationConfig';

export class IntegrationConfigService {
  static async token() {
    return API.get('auth/token');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static async getOrganizationInfo(_orgId: string) {
    return API.get('organizations/orgInfo');
  }

  /**
   * Get the list of providers available for the Marketplace
   * @returns {Array} list of providers
   */
  static async getProviders() {
    return API.get('providers');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async saveConfiguration(_orgId: string, type: string, data: any) {
    return API.post(`organizations/configuration/${type}`, data);
  }

  static async disableConfiguration(_orgId: string, type: string) {
    return API.delete('organizations', { integratorType: type });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async loginProviderConfiguration(orgId: string, type: string, orgConfig: any) {
    return API.post(`auth/${type}/login/${orgId}`, orgConfig);
  }

  static async enableConfiguration(_orgId: string, type: string, apiKeyId: string) {
    return API.post('organizations', {
      integratorType: type,
      apiKeyId,
    });
  }

  static async validateGoogleAPI(newOrgConfig: IntegrationConfig) {
    return API.post('providers/gmaps/validate/validate-api-key', { newOrgConfig });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static async getOrganizationApiKey(_orgId: string) {
    const response = await API.get('organizations/apikey', {});
    if (response.statusCode === 404) {
      throw new Error('No API Keys found');
    }
    return response;
  }

  static async enableProviderConfig(_orgId: string, type: string) {
    return API.put(`organizations/${type}/enable`, {});
  }

  static async disableProviderConfig(_orgId: string, type: string) {
    return API.put(`organizations/${type}/disable`, {});
  }

  static async searchLogs(
    _orgId: string,
    integratorType: string,
    level: string[],
    dates: TimeFilterProps,
    pagination: Pagination,
  ) {
    return API.get('organizations/logs', {
      integratorType,
      level,
      dates,
      pagination: {
        ...pagination,
      },
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static async getTeamsConfiguration(_orgId: string) {
    return API.get('organizations/teams', {});
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static async getWorkersConfiguration(_orgId: string) {
    return API.get('organizations/workers', {});
  }

  static async getManifestInformation(provider: string, data: Manifest) {
    return API.get(`api/v2/integrations/${provider}/generate`, data);
  }

  /**
   * Method to request for provider location configuration
   * @param orgId organization id
   * @param integratorType provider type
   * @returns {Array} id and name for locations configured in provider.
   */
  static async getLocationsConfiguration(orgId: string, integratorType: string) {
    return API.get(`auth/${orgId}/${integratorType}/locations`, {});
  }

  /**
   * Method to execute the DELETE request
   * @param orgId Organization id
   * @param type Provider Name
   * @returns Return configuration from the delete provider.
   */
  static async deleteApplication(orgId: string, type: string) {
    return API.delete(`auth/${orgId}/${type}`, {});
  }

  /**
 * Retrieves Airtable bases for the organization.
 *
 * @param {string} orgId - The organization ID.
 * @returns {Promise} A promise that resolves with the Airtable bases.
 */
  static async getAirtableBases(orgId: string) {
    return API.get('providers/airtable/bases', { orgId });
  }

  /**
 * Retrieves WhenIWork positions for the organization.
 * @param {string} orgId - The organization ID.
 * @returns {Promise} A promise that resolves with the WhenIWork positions.
 */
  static async getWIWPositions(orgId: string) {
    return API.get('providers/wheniwork/positions', { orgId });
  }

  /**
 * Syncs WhenIWork users and Onfleet workers for the organization.
 * @param {string} orgId - The organization ID.
 * @returns {Promise} A promise that resolves with the Airtable bases.
 */
  static async resyncWIWUsers(orgId: string) {
    return API.get('providers/wheniwork/sync', { orgId });
  }
}
